import { useCallback, useState } from 'react'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

import { runOperation } from 'clients/exception-handling'
import type { AgentInsightService } from 'features/trend-detection/features/insight-agent/service'
import {
  ResearchAgentsAction,
  trackResearchAgents,
} from 'features/trend-detection/mixpanel'

export interface UseGeneralAgentCreateSubmitParams {
  projectId: string | null
  service: AgentInsightService
  name: string
  prompt: string
}

export interface UseGeneralAgentCreateSubmitResult {
  submit: () => Promise<boolean>
  isSubmitting: boolean
}

/** Reports (general) Agent's create-and-run. Backend wiring is a no-op
 * placeholder still, mirrored from the deleted `handleCreateGeneralAgent` —
 * see `api/features/features/reports_agent/DESIGN.md`. */
export function useGeneralAgentCreateSubmit({
  projectId,
  service,
  name,
  prompt,
}: UseGeneralAgentCreateSubmitParams): UseGeneralAgentCreateSubmitResult {
  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const submit = useCallback(async (): Promise<boolean> => {
    if (!projectId || isSubmitting) return false
    setIsSubmitting(true)
    trackResearchAgents(ResearchAgentsAction.agent_create, {
      agent_type: 'general',
      agent_name: name,
    })
    const created = await runOperation(
      () => service.createAgent(projectId, name, prompt),
      { operation: 'agent.general.create' },
    )
    if (created.status !== 'succeeded') {
      setIsSubmitting(false)
      if (created.status !== 'cancelled')
        toast.error('Could not create the insight agent. Please try again.')
      return false
    }
    const agent = created.value
    const started = await runOperation(
      () => service.runAgent(projectId, agent.uuid),
      { operation: 'agent.general.run', cleanup: () => setIsSubmitting(false) },
    )
    void queryClient.invalidateQueries(['insight-agents', projectId])
    if (started.status !== 'succeeded') {
      toast.warn(
        'Agent created, but its run did not start. Open the agent to run it again.',
      )
      return true
    }
    toast.success(
      `"${agent.name}" is running. Its insights will appear in the ` +
        'Agent insights tab of the insights feed when it finishes.',
    )
    return true
  }, [projectId, isSubmitting, service, name, prompt, queryClient])

  return { submit, isSubmitting }
}
